<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import ProductComposition from "./ProductComposition.vue";
import ProductColors from "./ProductColors.vue";
import ProductSizes from "./ProductSizes.vue";
import ProductBulkBar from "./ProductBulkBar.vue";
import ShippingFreeBar from "./ShippingFreeBar.vue";

const props = defineProps({
  products: {
    type: Array,
    required: true
  },
  sectionContext: {
    type: String,
    required: true
  }
});

const store = useStore();
const selectedProduct = computed(() => store.getters["product/selectedProduct"]);
const selectedComposition = computed(() => store.getters["product/selectedComposition"]);
const isCompositionSelected = computed(
  () => !!props.products.find(({ id }) => selectedProduct.value?.id === id)
);

// TODO: remove this condition when we're back to bulk discount display
const isBulkDiscountAvailable = false;
</script>

<template>
  <div v-if="props.products.length" class="product-pieces">
    <h4 class="product-pieces__title">
      <slot></slot>
    </h4>
    <div class="product-pieces__items">
      <template v-for="(product, index) in props.products" :key="product.id">
        <ProductComposition
          v-if="product.compositions.length > 0"
          :product="product"
          :position="index"
          :section-context="props.sectionContext"
        />
      </template>
    </div>
    <Transition
      name="composition"
      enter-active-class="product-pieces__composition--enter-active"
      leave-active-class="product-pieces__composition--leave-active"
      enter-from-class="product-pieces__composition--enter-from"
      leave-to-class="product-pieces__composition--leave-to"
    >
      <div v-if="isCompositionSelected" class="product-pieces__composition">
        <ProductColors
          :key="selectedProduct.id"
          :selected-composition="selectedComposition"
          :compositions="selectedProduct.compositions"
        />
        <ProductSizes
          :variants="selectedComposition.variants"
          :product="selectedProduct"
          :composition="selectedComposition"
        />

        <div class="product-pieces__shipping">
          <div class="product-pieces__shipping-block">
            <ShippingFreeBar />
          </div>
          <div v-if="isBulkDiscountAvailable" class="product-pieces__shipping-block">
            <ProductBulkBar />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.product-pieces {
  padding: 0 16px;
  margin-top: 32px;
  position: relative;
  z-index: 0; // To prevent overlapping with sticky box. ".product-composition__selected-delimiter" has higher z-index

  &__title {
    font-size: 16px;
    font-weight: 500;
  }

  &__items {
    display: flex;
    gap: 8px;
    overflow-x: scroll;
    padding: 20px 0;
  }

  &__composition {
    border: $composition-border-width solid $blue;
    padding: 4px;
    background: $pod-gray-modern-100;
    border-radius: 6px;
    position: relative;
    top: -2px;

    &--enter-active {
      transition: all 0.5s ease-out;
    }

    &--leave-active {
      transition: all 0.5s ease-out;
    }

    &--enter-from,
    &--leave-to {
      transform: translateY(-20px);
      opacity: 0;
    }

    @include with-desktop {
      padding: 24px;
    }
  }

  &__shipping {
    @include with-tablet {
      display: flex;
      align-items: center;
      padding: 20px 0 0;
      gap: 24px;
    }
  }

  &__shipping-block {
    padding: 0 16px;
    margin: 20px auto 0;
    min-height: 60px;
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;

    @include with-tablet {
      flex: 1;
      padding: 0;
      margin: 0;
    }
  }
}
</style>
